/**
 * Stream Throughput & Network Quality Tracker
 * 
 * Specifically measures "Received Throughput" (Actual payload data received by frontend):
 * - HLS: Aggregate HTTP segment payload bytes over a sliding time window.
 * - WebRTC: Delta inbound-rtp bytesReceived over delta time.
 * - Diagnostic metrics: Packet loss, jitter, frames dropped, FPS.
 */

/**
 * Format bits per second (bps) into human-readable throughput strings
 * @param {number} bps - Bits per second
 * @param {'bits'|'bytes'} mode - Format as Mbps/Kbps or MB/s / KB/s
 * @returns {string} e.g. "2.45 Mbps" or "310 KB/s"
 */
export function formatThroughput(bps, mode = 'bits') {
  if (!bps || isNaN(bps) || bps <= 0) {
    return mode === 'bytes' ? '0 KB/s' : '0 Kbps';
  }

  if (mode === 'bytes') {
    const bytesPerSec = bps / 8;
    if (bytesPerSec >= 1024 * 1024) {
      return `${(bytesPerSec / (1024 * 1024)).toFixed(2)} MB/s`;
    }
    return `${Math.round(bytesPerSec / 1024)} KB/s`;
  }

  // Default: bits mode (Kbps / Mbps)
  if (bps >= 1000 * 1000) {
    return `${(bps / (1000 * 1000)).toFixed(2)} Mbps`;
  }
  return `${Math.round(bps / 1000)} Kbps`;
}

/**
 * HLS Throughput Tracker using a sliding time window
 */
export class HLSThroughputTracker {
  /**
   * @param {number} windowMs - Duration of sliding window in ms (default 6000ms = 6s)
   */
  constructor(windowMs = 6000) {
    this.windowMs = windowMs;
    this.samples = []; // Array of { timestamp: number, bytes: number }
    this.totalBytesLoaded = 0;
    this.intervalId = null;
    this.hlsInstance = null;
    this.fragLoadedHandler = null;
  }

  /**
   * Record a downloaded fragment chunk
   * @param {number} bytes - Number of bytes loaded in this fragment
   * @param {number} [timestamp] - Timestamp of download completion (defaults to Date.now())
   */
  addFragment(bytes, timestamp = Date.now()) {
    if (!bytes || bytes <= 0) return;
    this.samples.push({ timestamp, bytes });
    this.totalBytesLoaded += bytes;
    this._cleanOldSamples(timestamp);
  }

  _cleanOldSamples(now = Date.now()) {
    const cutoff = now - this.windowMs;
    this.samples = this.samples.filter((s) => s.timestamp >= cutoff);
  }

  /**
   * Calculate received throughput over the active window
   * @returns {{ bps: number, kbps: number, mbps: number, formatted: string, formattedBytes: string, sampleCount: number }}
   */
  getThroughput() {
    const now = Date.now();
    this._cleanOldSamples(now);

    if (this.samples.length === 0) {
      return {
        bps: 0,
        kbps: 0,
        mbps: 0,
        formatted: '0 Kbps',
        formattedBytes: '0 KB/s',
        sampleCount: 0,
      };
    }

    const totalBytesInWindow = this.samples.reduce((acc, s) => acc + s.bytes, 0);

    // Calculate elapsed time in window (effective span from oldest sample to now)
    const oldestTimestamp = this.samples[0].timestamp;
    const elapsedSec = Math.max(1, (now - oldestTimestamp) / 1000);
    const windowMaxSec = this.windowMs / 1000;
    const effectiveSec = Math.min(windowMaxSec, Math.max(elapsedSec, 1));

    const bps = (totalBytesInWindow * 8) / effectiveSec;
    const kbps = Math.round(bps / 1000);
    const mbps = parseFloat((bps / (1000 * 1000)).toFixed(2));

    return {
      bps,
      kbps,
      mbps,
      formatted: formatThroughput(bps, 'bits'),
      formattedBytes: formatThroughput(bps, 'bytes'),
      sampleCount: this.samples.length,
    };
  }

  /**
   * Attach directly to an Hls.js instance
   * @param {object} hls - Hls.js instance
   * @param {function} onUpdate - Callback called every interval with throughput stats
   * @param {number} intervalMs - Polling interval in ms (default: 1000)
   */
  attachHls(hls, onUpdate, intervalMs = 1000) {
    this.detachHls();
    if (!hls) return;

    this.hlsInstance = hls;
    this.fragLoadedHandler = (_event, data) => {
      const bytes = data?.stats?.loaded || data?.stats?.total || 0;
      if (bytes > 0) {
        this.addFragment(bytes);
      }
    };

    if (hls.on && hls.constructor && hls.constructor.Events) {
      hls.on(hls.constructor.Events.FRAG_LOADED, this.fragLoadedHandler);
    }

    if (typeof onUpdate === 'function') {
      this.intervalId = setInterval(() => {
        onUpdate(this.getThroughput());
      }, intervalMs);
    }
  }

  detachHls() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.hlsInstance && this.fragLoadedHandler) {
      try {
        if (this.hlsInstance.off && this.hlsInstance.constructor?.Events) {
          this.hlsInstance.off(
            this.hlsInstance.constructor.Events.FRAG_LOADED,
            this.fragLoadedHandler,
          );
        }
      } catch (_) { }
    }
    this.hlsInstance = null;
    this.fragLoadedHandler = null;
    this.samples = [];
  }

  reset() {
    this.samples = [];
    this.totalBytesLoaded = 0;
  }

  destroy() {
    this.detachHls();
    this.reset();
  }
}

/**
 * WebRTC Inbound Media Throughput & Diagnostics Tracker
 */
export class WebRTCThroughputTracker {
  constructor() {
    this.lastBytes = 0;
    this.lastTimestamp = 0;
    this.intervalId = null;
    this.peerConnection = null;
  }

  /**
   * Start polling RTCPeerConnection stats
   * @param {RTCPeerConnection} peerConnection 
   * @param {function} onUpdate - Callback receiving ({ throughput, quality })
   * @param {number} intervalMs - Polling interval (default: 1000ms)
   */
  start(peerConnection, onUpdate, intervalMs = 1000) {
    this.stop();
    if (!peerConnection || typeof peerConnection.getStats !== 'function') return;

    this.peerConnection = peerConnection;
    this.lastBytes = 0;
    this.lastTimestamp = 0;

    this.intervalId = setInterval(async () => {
      if (
        !this.peerConnection ||
        this.peerConnection.connectionState === 'closed' ||
        this.peerConnection.signalingState === 'closed'
      ) {
        this.stop();
        return;
      }

      try {
        const stats = await this.peerConnection.getStats();
        let videoInboundReport = null;
        let candidatePairReport = null;

        stats.forEach((report) => {
          if (report.type === 'inbound-rtp' && (report.kind === 'video' || report.mediaType === 'video')) {
            videoInboundReport = report;
          }
          if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
            candidatePairReport = report;
          }
        });

        if (videoInboundReport) {
          const currentBytes = videoInboundReport.bytesReceived || 0;
          const currentTimestamp = videoInboundReport.timestamp || Date.now();

          let bps = 0;
          if (this.lastBytes > 0 && this.lastTimestamp > 0 && currentTimestamp > this.lastTimestamp) {
            const deltaBytes = Math.max(0, currentBytes - this.lastBytes);
            const deltaSec = (currentTimestamp - this.lastTimestamp) / 1000;
            if (deltaSec > 0) {
              bps = (deltaBytes * 8) / deltaSec;
            }
          }

          this.lastBytes = currentBytes;
          this.lastTimestamp = currentTimestamp;

          const kbps = Math.round(bps / 1000);
          const mbps = parseFloat((bps / (1000 * 1000)).toFixed(2));

          const statsData = {
            throughput: {
              bps,
              kbps,
              mbps,
              formatted: formatThroughput(bps, 'bits'),
              formattedBytes: formatThroughput(bps, 'bytes'),
            },
            quality: {
              packetsLost: videoInboundReport.packetsLost || 0,
              jitter: videoInboundReport.jitter ? (videoInboundReport.jitter * 1000).toFixed(1) : 0,
              framesDropped: videoInboundReport.framesDropped || 0,
              framesDecoded: videoInboundReport.framesDecoded || 0,
              fps: videoInboundReport.framesPerSecond || 0,
              rtt: candidatePairReport?.currentRoundTripTime
                ? (candidatePairReport.currentRoundTripTime * 1000).toFixed(1)
                : null,
            },
          };

          if (typeof onUpdate === 'function') {
            onUpdate(statsData);
          }
        }
      } catch (err) {
      }
    }, intervalMs);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.peerConnection = null;
    this.lastBytes = 0;
    this.lastTimestamp = 0;
  }

  destroy() {
    this.stop();
  }
}
